import { Button } from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { useState } from 'react';
import { IST_ZONE, type OfficerTrailDto } from '@pams/shared';
import { OfficerSearchSelect } from '@/components/OfficerSearchSelect';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/queryKeys';
import { USAGE_ANALYTICS_COPY } from '../copy';
import { SubBlock } from './AnalyticsSection';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [ANALYTICS §6] AN OFFICER'S LAST FIFTEEN — THE ONLY PLACE IN THE PRODUCT THAT SHOWS THEM.
 *
 *  ⚠️ THE READ IS A `useMutation`, NEVER A `useQuery`, AND THAT IS THE AUDIT RATHER THAN A STYLE.
 *  `GET /analytics/officers/:id/trail` writes `OFFICER_TRAIL_VIEWED` every time it is answered, so
 *  one request must mean one deliberate look. A query would refetch on focus, on remount and on a
 *  stale timer — each an audit row the super admin never chose to write — and would leave the trail
 *  in a cache that outlives the screen. Duty Mitra's phone reveal (`CallControls.tsx`) is the
 *  precedent: no query key, no cache, the answer in component state and nowhere else.
 *
 *  ⚠️ THE RESULT IS KEPT WITH THE OFFICER IT BELONGS TO, and shown only while that officer is still
 *  the one chosen. Changing the officer clears it at once — and a response that lands after the
 *  choice moved on is never painted at all, under the new name OR under the old one on the way back.
 *
 *  ⚠️ SO THE RESULT IS SET BY A PER-CALL `mutate(…, { onSuccess })`, NEVER THE HOOK'S. MEASURED on
 *  @tanstack/query-core 5.101.4: `Mutation.execute` calls the HOOK-level `onSuccess` whether or not
 *  an observer is still attached, while the per-call callbacks run from the observer and `reset()`
 *  detaches it. Every change clears the result, so the hazard is one ordering: the viewer leaves an
 *  officer and chooses them again BEFORE the look answers — a hook-level setter would then store the
 *  late answer against the officer now chosen and paint a trail nobody clicked for in this sitting.
 *
 *  ⚠️ THE AUDIT LOG IS REFRESHED ON SETTLE — `queryKeys.audit.all`, the one thing that invalidates
 *  it — AND AT HOOK LEVEL, so it runs even after the choice moved on. The server commits
 *  `OFFICER_TRAIL_VIEWED` before it loads the officer's references and answers, so a failed or lost
 *  response can still leave a row under the viewer's name; a cached Audit log would otherwise hide
 *  it. On a refusal that wrote nothing, the refresh costs one refetch and hides nothing.
 *
 *  ⚠️ NO MAP LINK, NO EMBED, NO OVERLAY. Coordinates are text: handing a map service an officer's
 *  movements is a disclosure of its own, and the page test scans every attribute for one. And no
 *  `Tooltip`/`Dropdown`/`Drawer`/`Modal`/`Sheet`/`Dialog`, so `census.spec.ts` has nothing to sweep.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

const copy = USAGE_ANALYTICS_COPY.trail;

/** `14:05, 30 Aug 2026` in IST — the page never shows a device-local time. */
const istTimeDate = (instant: string): string =>
  DateTime.fromISO(instant, { zone: IST_ZONE }).toFormat('HH:mm, d LLL yyyy');

/** Five decimals is about a metre — as precise as a phone fix is, and no more. */
const degrees = (value: number): string => value.toFixed(5);

type Opened = { officerId: string; trail: OfficerTrailDto };

export const OfficerTrailPanel = () => {
  const queryClient = useQueryClient();
  const [officerId, setOfficerId] = useState<string | undefined>();
  const [opened, setOpened] = useState<Opened | null>(null);

  const open = useMutation({
    mutationFn: (id: string) => api.get<OfficerTrailDto>(`/analytics/officers/${id}/trail`),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.audit.all });
    },
  });

  const shown = opened !== null && opened.officerId === officerId ? opened.trail : null;
  const failedForThisOfficer = open.isError && open.variables === officerId;

  return (
    <section
      aria-labelledby="analytics-officer-trail"
      data-testid="analytics-officer-trail"
      className="flex flex-col gap-4 rounded-lg border border-border bg-card p-5"
    >
      <div className="flex flex-col gap-1">
        <h2 id="analytics-officer-trail" className="text-base font-medium text-heading">
          {copy.title}
        </h2>
        <p className="text-sm text-muted-foreground">{copy.intro}</p>
        <p className="text-sm text-foreground" data-testid="officer-trail-audit-notice">
          {copy.auditNotice}
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="w-full max-w-sm">
          <OfficerSearchSelect
            value={officerId}
            aria-label={copy.officerLabel}
            onChange={(next) => {
              setOfficerId(next);
              setOpened(null);
              /* ⚠️ Detaches the observer, so a look still in flight never reaches its per-call `onSuccess`. */
              open.reset();
            }}
          />
        </div>
        <Button
          type="primary"
          /* ⚠️ Disabled while a look is in flight, so a second click is visibly refused rather than
             silently discarded — and cannot become a second audit row. */
          disabled={officerId === undefined || open.isPending}
          loading={open.isPending}
          onClick={() => {
            if (officerId === undefined) return;
            const chosen = officerId;
            open.mutate(chosen, {
              onSuccess: (trail) => {
                setOpened({ officerId: chosen, trail });
              },
            });
          }}
        >
          {copy.show}
        </Button>
      </div>

      {failedForThisOfficer && (
        <p role="alert" className="text-sm text-destructive">
          {copy.failed}
        </p>
      )}

      {shown && (
        <div className="flex flex-col gap-4" data-testid="officer-trail-result">
          <p className="text-sm text-foreground">
            {[shown.officer.name, shown.officer.rank?.shortName, shown.officer.orgUnit?.name]
              .filter((part) => part !== undefined && part.length > 0)
              .join(' · ')}
            {shown.officer.isActive ? '' : ` ${copy.inactive}`}
          </p>

          <div className="grid gap-4 @min-[880px]:grid-cols-2">
            <SubBlock title={copy.addressesHeading} caption={copy.addressesCaption}>
              {shown.addresses.length === 0 ? (
                <p className="text-sm text-muted-foreground">{copy.noAddresses}</p>
              ) : (
                <ol className="flex flex-col gap-1 text-sm" data-testid="officer-trail-addresses">
                  {shown.addresses.map((entry) => (
                    <li key={`${entry.ip}|${entry.observedAt}`} className="break-words">
                      {copy.address(entry.ip, istTimeDate(entry.observedAt))}
                    </li>
                  ))}
                </ol>
              )}
            </SubBlock>

            <SubBlock title={copy.locationsHeading} caption={copy.locationsCaption}>
              {shown.locations.length === 0 ? (
                <p className="text-sm text-muted-foreground">{copy.noLocations}</p>
              ) : (
                <ol className="flex flex-col gap-1 text-sm" data-testid="officer-trail-locations">
                  {shown.locations.map((entry, index) => (
                    <li key={`${entry.recordedAt}#${String(index)}`} className="break-words">
                      {[
                        copy.coordinates(degrees(entry.lat), degrees(entry.lng)),
                        copy.accuracy(entry.accuracyMetres),
                        copy.recorded(istTimeDate(entry.recordedAt)),
                        copy.deviceTime(istTimeDate(entry.capturedAt)),
                      ].join(' · ')}
                    </li>
                  ))}
                </ol>
              )}
            </SubBlock>
          </div>

          <p className="text-xs text-muted-foreground">{copy.retention(shown.retentionDays)}</p>
        </div>
      )}
    </section>
  );
};
